import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { usePrice } from "@/lib/price";

const buildFaqs = (price, cap) => [
  {
    q: "Is this another course?",
    a: "No. Courses hand you lectures; JobHacki hands you finished artifacts — playbooks you follow, a resume builder you use, and live job listings you apply to. There's nothing to get through.",
  },
  {
    q: "What's free and what's paid?",
    a: `A free account (no card) unlocks the Playbook Directory, the Job Directory (live roles), Resume Match (score your resume vs live jobs), and the Toolbox. All-Access (${price} one-time) adds the Resume Builder, the Readiness Simulator, OmniCut, OmniScript (when it ships), and every tool we ship next.`,
  },
  {
    q: "Do I need Claude, ChatGPT or API keys?",
    a: "No setup, no keys, no repos. Log in and use it. One exception: OmniCut runs as a custom GPT, so that single tool needs a ChatGPT account.",
  },
  {
    q: "Will the price really rise?",
    a: `Yes. The first ${(cap || 2500).toLocaleString()} members lock ${price}, then the one-time price climbs toward $197. There is no countdown timer because we don't fake urgency.`,
  },
  {
    q: "How fresh is the Job Directory?",
    a: "Live jobs pulled directly from 18,000+ employer ATS boards (Greenhouse, Lever, Ashby, Workday, Indeed-Direct, ADP, etc.) on a continuous schedule — not reposted from aggregators. Save searches, set alerts, apply direct, no middleman.",
  },
  {
    q: "Is it really one-time?",
    a: `Yes. JobHacki is a single ${price} payment for lifetime access — not a subscription. There is nothing to cancel and no recurring charge.`,
  },
  {
    q: "How do I log in?",
    a: "With the email you joined with. After checkout we email you a secure link to set your password — from then on it's just email and password at your dashboard.",
  },
  {
    q: "How fast do I get access?",
    a: "Instantly. The moment your payment clears, your account unlocks — log in and every tool is right there. No manual approval, no waiting.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: `Yes. If JobHacki isn't for you, request a refund within 7 days through Whop — no questions asked. At ${price} we'd rather you be happy than hold your money.`,
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  const { price, cap } = usePrice();
  const faqs = buildFaqs(price, cap);
  return (
    <section
      id="faq"
      data-testid="faq"
      className="border-b-4 border-[#0a0a0a] bg-[#FDFBF7] scroll-mt-24"
    >
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="text-center mb-12 reveal">
          <div className="font-accent text-sm bg-[#0a0a0a] text-[#FFE600] inline-block px-3 py-1 mb-4">
            FAQ
          </div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl">
            The questions
            <br />
            people <span className="bg-[#0055FF] text-white px-2">actually ask.</span>
          </h2>
        </div>

        <div className="space-y-5">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                data-testid={`faq-item-${i}`}
                className={`border-4 border-[#0a0a0a] shadow-brutal ${
                  isOpen ? "bg-[#FFE600]" : "bg-white"
                }`}
              >
                <button
                  data-testid={`faq-trigger-${i}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full text-left px-5 md:px-7 py-5 md:py-6 flex items-center justify-between gap-6"
                >
                  <h3 className="font-display text-xl md:text-2xl leading-tight m-0">
                    {f.q}
                  </h3>
                  <span className="shrink-0 w-10 h-10 bg-[#0a0a0a] text-[#FFE600] flex items-center justify-center">
                    {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                {isOpen && (
                  <div
                    data-testid={`faq-content-${i}`}
                    className="px-5 md:px-7 pb-6 font-mono-body text-sm md:text-base leading-relaxed text-[#0a0a0a]/85 border-t-2 border-dashed border-[#0a0a0a]/40 pt-4"
                  >
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
